import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Modal, Form, Input, message, Popconfirm, Space, Card, DatePicker, InputNumber, Select
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, HistoryOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Driver, CreateDriver, Vehicle } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { vehicleApi, driverApi, feeStructureApi } from '../services/api';

const { Option } = Select;

interface SalaryHistoryItem {
  id: number;
  salaryRate: number;
  effectiveDate: string;
  createdAt: string;
}

const DriverPage: React.FC = () => {
  const { t } = useLanguage();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [defaultSalaryRate, setDefaultSalaryRate] = useState<number>(0);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [salaryHistory, setSalaryHistory] = useState<SalaryHistoryItem[]>([]);
  const [historyDriver, setHistoryDriver] = useState<Driver | null>(null);
  const [form] = Form.useForm();

  const fetchDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await driverApi.getAll();
      setDrivers(res.data);
    } catch (error) {
      message.error(t('failed.to.fetch.drivers'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchVehicles = useCallback(async () => {
    try {
      const res = await vehicleApi.getAll();
      setVehicles(res.data);
    } catch {
      message.error(t('failed.to.fetch.vehicles'));
    }
  }, [t]);

  // Fetch system default salary rate
  const fetchDefaultSalaryRate = useCallback(async () => {
    try {
      const res = await feeStructureApi.getActive('DEFAULT_DRIVER_SALARY_RATE');
      setDefaultSalaryRate(res.data.rate * 100);
    } catch {
      setDefaultSalaryRate(0);
    }
  }, []);

  useEffect(() => {
    fetchDrivers();
    fetchVehicles();
    fetchDefaultSalaryRate();
  }, [fetchDrivers, fetchVehicles, fetchDefaultSalaryRate]);

  // Helper to get latest salary history for a driver
  const fetchLatestSalaryHistory = async (driverId: number) => {
    try {
      const res = await driverApi.getSalaryHistory(driverId);
      if (res.data && res.data.length > 0) {
        return res.data[0]; // latest by effectiveDate desc
      }
    } catch {}
    return null;
  };

  const handleAdd = () => {
    setEditingDriver(null);
    form.resetFields();
    form.setFieldsValue({
      salaryRate: defaultSalaryRate,
      affectedDate: dayjs(),
    });
    setModalVisible(true);
  };

  const handleEdit = async (driver: Driver) => {
    setEditingDriver(driver);
    let salaryRatePercent = undefined;
    let affectedDate = undefined;
    // Fetch latest salary history for this driver
    const latestHistory = await fetchLatestSalaryHistory(driver.id);
    if (latestHistory) {
      salaryRatePercent = latestHistory.salaryRate * 100;
      affectedDate = dayjs(latestHistory.effectiveDate);
    }
    const formData = {
      ...driver,
      licenseExpiryDate: driver.licenseExpiryDate ? dayjs(driver.licenseExpiryDate) : undefined,
      dateOfBirth: driver.dateOfBirth ? dayjs(driver.dateOfBirth) : undefined,
      salaryRate: salaryRatePercent,
      affectedDate: affectedDate,
      preferredVehicleIds: driver.preferredVehicleIds || [],
    };
    form.setFieldsValue(formData);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await driverApi.delete(id);
      message.success(t('driver.deleted.successfully'));
      fetchDrivers();
    } catch (error) {
      message.error(t('error.deleting.driver'));
    }
  };

  const handleShowHistory = async (driver: Driver) => {
    setHistoryDriver(driver);
    setHistoryModalVisible(true);
    try {
      const res = await driverApi.getSalaryHistory(driver.id);
      setSalaryHistory(res.data);
    } catch {
      setSalaryHistory([]);
      message.error(t('failed.to.fetch.salary.history'));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      // Convert percent to decimal before sending
      const submitData: CreateDriver & { effectiveDate?: string } = {
        ...values,
        licenseExpiryDate: values.licenseExpiryDate ? values.licenseExpiryDate.toISOString() : undefined,
        dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toISOString() : undefined,
        salaryRate: values.salaryRate !== undefined && values.salaryRate !== null ? values.salaryRate / 100 : undefined,
        preferredVehicleIds: values.preferredVehicleIds || [],
        effectiveDate: values.affectedDate ? values.affectedDate.toISOString() : undefined,
      };
      if (editingDriver) {
        await driverApi.update(editingDriver.id, submitData);
        message.success(t('driver.updated.successfully'));
      } else {
        await driverApi.create(submitData);
        message.success(t('driver.created.successfully'));
      }
      setModalVisible(false);
      form.resetFields();
      fetchDrivers();
    } catch (error) {
      message.error(editingDriver ? t('error.updating.driver') : t('error.creating.driver'));
    }
  };

  const handleDeleteSalaryHistory = async (id: number) => {
    try {
      await driverApi.deleteSalaryHistory(id);
      message.success(t('deleted.successfully'));
      if (historyDriver) {
        const res = await driverApi.getSalaryHistory(historyDriver.id);
        setSalaryHistory(res.data);
      }
    } catch {
      message.error(t('failed.to.delete.salary.history'));
    }
  };

  const columns: ColumnsType<Driver> = [
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: t('phone'),
      dataIndex: 'phone',
      key: 'phone'
    },
    {
      title: t('email'),
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: t('license.number'),
      dataIndex: 'licenseNumber',
      key: 'licenseNumber'
    },
    {
      title: t('license.expiry'),
      dataIndex: 'licenseExpiryDate',
      key: 'licenseExpiryDate',
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-'
    },
    {
      title: t('date.of.birth'),
      dataIndex: 'dateOfBirth',
      key: 'dateOfBirth',
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : '-'
    },
    {
      title: t('salary.rate'),
      dataIndex: 'salaryRate',
      key: 'salaryRate',
      render: (value) => value !== undefined && value !== null ? `${(value * 100).toFixed(2)}%` : '-'
    },
    {
      title: t('preferred.vehicle'),
      dataIndex: 'preferredVehiclePlates',
      key: 'preferredVehiclePlates',
      render: (plates: string[]) => plates && plates.length > 0 ? plates.join(', ') : '-'
    },
    {
      title: t('created.by'),
      dataIndex: 'createdByUserName',
      key: 'createdByUserName'
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Button
            icon={<HistoryOutlined />}
            onClick={() => handleShowHistory(record)}
            size="small"
          >
            {t('history')}
          </Button>
          <Popconfirm
            title={t('delete.confirm')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('yes')}
            cancelText={t('no')}
          >
            <Button
              icon={<DeleteOutlined />}
              danger
              size="small"
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>{t('driver.management')}</h2>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            {t('add.driver')}
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={drivers}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: true }}
        />

        <Modal
          title={editingDriver ? t('edit.driver') : t('add.driver')}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          width={800}
        >
          <Form
            form={form}
            layout="horizontal"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            onFinish={handleSubmit}
          >
            <Form.Item
              label={t('name')}
              name="name"
              rules={[{ required: true, message: t('required.field') }]}
            >
              <Input maxLength={200} />
            </Form.Item>

            <Form.Item
              label={t('phone')}
              name="phone"
              rules={[{ required: true, message: t('required.field') }]}
            >
              <Input maxLength={100} />
            </Form.Item>

            <Form.Item
              label={t('email')}
              name="email"
              rules={[{ type: 'email', message: t('invalid.format') }]}
            >
              <Input maxLength={200} />
            </Form.Item>

            <Form.Item
              label={t('license.number')}
              name="licenseNumber"
            >
              <Input maxLength={50} />
            </Form.Item>

            <Form.Item
              label={t('license.expiry')}
              name="licenseExpiryDate"
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label={t('date.of.birth')}
              name="dateOfBirth"
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label={t('salary.rate') + ' (%)'}
              name="salaryRate"
              rules={[{ type: 'number', min: 0, max: 100, message: t('invalid.salary.rate') }]}
            >
              <InputNumber min={0} max={100} step={0.01} style={{ width: '100%' }} addonAfter="%" />
            </Form.Item>

            <Form.Item
              label={t('affected.date')}
              name="affectedDate"
              rules={[{ required: true, message: t('required.field') }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label={t('preferred.vehicle')}
              name="preferredVehicleIds"
            >
              <Select
                mode="multiple"
                placeholder={t('please.select')}
                allowClear
                optionFilterProp="children"
                showSearch
              >
                {vehicles.map(vehicle => (
                  <Option key={vehicle.id} value={vehicle.id}>
                    {vehicle.licensePlate} - {vehicle.brand} {vehicle.model}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={t('address')}
              name="address"
            >
              <Input maxLength={500} />
            </Form.Item>

            <Form.Item
              label={t('notes')}
              name="notes"
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={historyDriver ? `${t('salary.rate.history')} - ${historyDriver.name}` : t('salary.rate.history')}
          open={historyModalVisible}
          onCancel={() => setHistoryModalVisible(false)}
          footer={null}
          width={600}
        >
          <Table
            dataSource={salaryHistory}
            rowKey="id"
            pagination={false}
            columns={[
              {
                title: t('salary.rate'),
                dataIndex: 'salaryRate',
                key: 'salaryRate',
                render: (value: number) => `${(value * 100).toFixed(2)}%`
              },
              {
                title: t('effective.date'),
                dataIndex: 'effectiveDate',
                key: 'effectiveDate',
                render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm')
              },
              {
                title: t('created.at'),
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm')
              },
              {
                title: t('actions'),
                key: 'actions',
                render: (_, record) => (
                  <Popconfirm
                    title={t('delete.confirm')}
                    onConfirm={() => handleDeleteSalaryHistory(record.id)}
                    okText={t('yes')}
                    cancelText={t('no')}
                  >
                    <Button danger size="small">{t('delete')}</Button>
                  </Popconfirm>
                )
              }
            ]}
          />
        </Modal>
      </Card>
    </div>
  );
};

export default DriverPage;